import { useEffect, useMemo, useState } from 'react';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';

import NoAlcohol from 'assets/images/questionnaire/dont-drink.png';
import Ocasionally from 'assets/images/questionnaire/occasionally.png';
import OnWeekends from 'assets/images/questionnaire/on-weekends.png';
import FourOrMore from 'assets/images/questionnaire/four-or-more.png';
import { AnimatePresence, motion } from 'framer-motion';
import { Slider } from 'components/ui/slider';

const spanishConsumptions = [
  'No tomo alcohol',
  'Ocasionalmente',
  'Los fines de semana',
  '4 o más veces por semana',
];
const englishConsumptions = [
  'I don’t drink alcohol',
  'Occasionally',
  'On weekends',
  '4 or more times a week',
];

const getCountText = (count: number) => {
  if (count <= 6) {
    return { en: 'Less than 7', es: 'Menos de 7' };
  }
  if (count <= 10.5) {
    return { en: 'Between 7 and 14', es: 'Entre 7 y 14' };
  }
  return { en: '14 or more', es: 'Más de 14' };
};

const textToCountMapper = {
  'Less than 7': 6,
  'Between 7 and 14': 10.5,
  '14 or more': 15,
};
const spanishTextToCountMapper = {
  'Menos de 7': 6,
  'Entre 7 y 14': 10.5,
  'Más de 14': 15,
};

const ConsumptionImages = [NoAlcohol, Ocasionally, OnWeekends, FourOrMore];

export default function AlcoholConsumption() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const Consumptions = {
    NEVER: isSpanish ? spanishConsumptions[0] : englishConsumptions[0],
    OCCASIONALYY: isSpanish ? spanishConsumptions[1] : englishConsumptions[1],
    ON_WEEKENDS: isSpanish ? spanishConsumptions[2] : englishConsumptions[2],
    FOUR_OR_MORE: isSpanish ? spanishConsumptions[3] : englishConsumptions[3],
  };

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [moreConsumption, setMoreConsumption] = useState<number>(6);
  const [selectedOccurence, setSelectedOccurence] = useState<string>(
    Consumptions.NEVER
  );

  const englishChoices = JSON.parse(questionData?.eng_choices || '[]');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '[]');

  const options = useMemo(() => {
    const parsedEnglishChoices = englishChoices?.map((choice: any) => {
      if (typeof choice !== 'string') {
        return Object.keys(choice)[0];
      }
      return choice;
    });
    const parsedSpanishChoices = spanishChoices?.map((choice: any) => {
      if (typeof choice !== 'string') {
        return Object.keys(choice)[0];
      }
      return choice;
    });
    return {
      en: parsedEnglishChoices,
      es: parsedSpanishChoices,
    };
  }, [questionData]);
  const currentOptions = options?.[isSpanish ? 'es' : 'en'];

  useEffect(() => {
    if (questionData?.user_eng_choices || questionData?.user_spanish_choices) {
      const userChoices =
        questionData?.[isSpanish ? 'user_spanish_choices' : 'user_eng_choices'];

      let answer = '';
      try {
        const parsedChoices = JSON.parse(userChoices);
        answer = parsedChoices[0];
      } catch (error) {
        console.log(error);
      }

      if (typeof answer === 'object') {
        const [occurence, consumption] = Object.entries(answer)[0];
        setSelectedOccurence(occurence.trim() as string);
        const countMapper = isSpanish
          ? spanishTextToCountMapper
          : textToCountMapper;
        setMoreConsumption(
          countMapper[
            (consumption as string).trim() as keyof typeof countMapper
          ]
        );
        return;
      }

      const isObj = answer.includes(':');
      if (!isObj) {
        setSelectedOccurence((answer as string) || Consumptions?.NEVER);
      } else {
        const [occurence, consumption] = answer.split(':');

        setSelectedOccurence(occurence.trim() as string);
        const countMapper = isSpanish
          ? spanishTextToCountMapper
          : textToCountMapper;
        setMoreConsumption(
          countMapper[consumption.trim() as keyof typeof countMapper]
        );
      }
    }
  }, [Consumptions?.NEVER, isSpanish, questionData]);

  const onContinue = async () => {
    let choice = '';
    let spanishChoice = '';
    if (
      [Consumptions.NEVER, Consumptions.OCCASIONALYY].includes(
        selectedOccurence
      )
    ) {
      choice = JSON.stringify([englishConsumptions[selectedIndex]]);
      spanishChoice = JSON.stringify([spanishConsumptions[selectedIndex]]);
    } else {
      const { en: enConsumption, es: esConsumption } =
        getCountText(moreConsumption);
      choice = JSON.stringify([
        { [englishConsumptions[selectedIndex]]: enConsumption },
      ]);
      spanishChoice = JSON.stringify([
        { [spanishConsumptions[selectedIndex]]: esConsumption },
      ]);
    }

    onSubmit({
      choice_value: choice,
      spanish_choice_value: spanishChoice,
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={8}
    >
      <div className='relative grid items-start grid-cols-2 gap-4 lg:grid-cols-4 xl:gap-10 xl:grid-cols-5'>
        {currentOptions?.map((option: string, index: number) => {
          const isSelected = selectedIndex === index;

          return (
            <div
              key={option}
              className='relative grid cursor-pointer place-items-center'
              onClick={() => {
                setSelectedIndex(index);
                setSelectedOccurence(option);
              }}
            >
              {isSelected && (
                <motion.div
                  layoutId='body-type-indicator'
                  className='absolute z-0 size-28 md:size-32 lg:size-36 top-0 rounded-[24px] border-[2px] border-primary bg-primary/10'
                  transition={{
                    type: 'spring',
                    stiffness: 350,
                    damping: 40,
                  }}
                />
              )}

              <div
                className={cn(
                  'relative z-10 grid border-[2px] place-items-center size-28 md:size-32 lg:size-36 rounded-[24px] transition-all duration-300',
                  isSelected && 'border-transparent'
                )}
              >
                <img
                  src={ConsumptionImages[index]}
                  alt={option}
                  className='object-contain w-full h-16 md:h-20 lg:h-24'
                />
              </div>

              <p
                className={cn(
                  'font-medium capitalize text-center leading-tight mt-4 text-sm z-10 lg:w-40 transition-all duration-150',
                  isSelected ? 'text-primary' : 'text-gray-400'
                )}
              >
                {option}
              </p>
            </div>
          );
        })}
      </div>
      <AnimatePresence>
        {selectedIndex >= 2 ? (
          <motion.div
            key='more-consumption'
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className='grid pt-8 overflow-hidden xl:grid-cols-5 md:mx-10 lg:mx-0'
          >
            <div className='col-span-1 space-y-2 lg:col-span-4'>
              <Slider
                max={15}
                min={6}
                step={1.5}
                value={[moreConsumption]}
                onValueChange={(value) => {
                  setMoreConsumption(value[0]);
                }}
              />
              <div className='flex items-center justify-between w-full text-sm font-medium text-gray-400'>
                <p>{'<7'}</p>
                <p>7 to 14</p>
                <p>{'>14'}</p>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </QuestionnaireContainer>
  );
}
